import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui";
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  UserPlus,
  Building2,
  LogIn,
  Calendar,
  CreditCard,
  Trophy,
  ClipboardList,
  Lightbulb,
  AlertTriangle,
  ShieldCheck,
  Mail,
  Smartphone,
  PlayCircle,
  Link2,
} from "lucide-react";

/**
 * Guía paso a paso para la demo del campamento.
 *
 * Pública en /guia — pensada para que cada director / miembro la siga
 * en su propio dispositivo durante (y después) de la sesión en vivo.
 */

// ============================================================================
// TIPOS
// ============================================================================

type Track = "director" | "miembro";

interface SubStep {
  title: string;
  detail: string;
  tip?: string;
  warning?: string;
}

interface Step {
  id: string;
  number: number;
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  subtitle: string;
  goal: string;
  prerequisites?: string[];
  substeps: SubStep[];
  expectedResult: string;
  cta?: { label: string; to: string; external?: boolean };
}

// ============================================================================
// CONTENIDO — TRACK DIRECTOR
// ============================================================================

const DIRECTOR_STEPS: Step[] = [
  {
    id: "cuenta-club",
    number: 1,
    icon: UserPlus,
    title: "Crea tu cuenta y registra tu club",
    subtitle: "Empezamos por aquí: necesitas una cuenta de director y un club.",
    goal: "Al final de este paso tendrás una cuenta activa y tu club registrado, esperando aprobación o ya aprobado.",
    prerequisites: [
      "Un correo electrónico personal o del club (al que tengas acceso AHORA).",
      "Nombre oficial de tu club.",
      "Iglesia o congregación a la que pertenece el club.",
    ],
    substeps: [
      {
        title: "Abre la página de registro",
        detail:
          'Ve a la página principal y haz clic en "Acceder al sistema" o "Registrarme". También puedes ir directo a /register.',
      },
      {
        title: "Llena tus datos personales",
        detail:
          "Nombre completo, correo, contraseña (mínimo 8 caracteres). Usa un correo real porque te llegará un enlace de verificación.",
        warning:
          "No uses correos genéricos del club que comparte mucha gente. La cuenta es TUYA como director.",
      },
      {
        title: "Verifica tu correo",
        detail:
          "Revisa tu bandeja de entrada (y SPAM). Haz clic en el enlace que te llegó. Sin esto NO puedes continuar.",
        tip: "Si no llegó en 2 minutos, revisa SPAM o pide reenviarlo desde la pantalla de verificación.",
      },
      {
        title: "Selecciona tu rol: Director de club",
        detail:
          'En la pantalla de "Configuración inicial" elige "Soy director de un club".',
      },
      {
        title: "Registra tu club",
        detail:
          "Completa: nombre del club, iglesia, tipo (Aventureros / Conquistadores / Guías Mayores), año de fundación si lo sabes.",
        tip: "Si tu iglesia no aparece en la lista, escribe el nombre exacto y se creará pendiente de validación.",
      },
      {
        title: "Espera la aprobación",
        detail:
          'Verás la pantalla "Pendiente de aprobación de club". El admin de tu asociación valida y aprueba (te avisaremos en la demo cuando aprobemos a todos).',
      },
    ],
    expectedResult:
      "Cuenta verificada + club creado. Cuando el admin lo apruebe, automáticamente entras al Dashboard.",
    cta: { label: "Ir a registrarme", to: "/register" },
  },
  {
    id: "miembros",
    number: 2,
    icon: Link2,
    title: "Invita a tus miembros con tu enlace de club",
    subtitle:
      "La forma RECOMENDADA: comparte el link de invitación y deja que cada miembro (o sus padres) cree su propia cuenta. Tú solo apruebas.",
    goal: "Que cada miembro de tu club tenga SU propia cuenta en Insignia, vinculada a tu club, sin que tú tengas que cargar todos los datos a mano.",
    prerequisites: [
      "Tu club ya aprobado (paso 1 completo).",
      "Acceso a tu grupo de WhatsApp / Telegram / correo del club.",
    ],
    substeps: [
      {
        title: "Inicia sesión y entra al Dashboard",
        detail:
          "Ve a /login con el correo y contraseña que registraste. En el Dashboard verás una tarjeta destacada con el código y enlace de invitación de tu club.",
      },
      {
        title: "Copia tu enlace de invitación",
        detail:
          'Tu enlace luce así: https://club-manager-col.web.app/setup?invite=XXXXXX donde XXXXXX es el código único de tu club. Usa el botón "Copiar enlace" o "Compartir por WhatsApp".',
        tip: "El código lo puedes regenerar si crees que se filtró, pero entonces los enlaces viejos dejarán de funcionar.",
      },
      {
        title: "Compártelo en tu grupo del club",
        detail:
          'Mensaje sugerido: "Hola familia del club! Regístrate aquí para que estés formalmente inscrito y puedas asistir al campamento: <pega el enlace>. Es 1 sola vez. Si tu hijo es menor, lo registras tú con tu correo."',
        tip: "Pónle un título claro al mensaje y fija el mensaje en el grupo de WhatsApp para que no se pierda.",
      },
      {
        title: "Cada miembro crea su cuenta solo",
        detail:
          "Cuando alguien abre tu enlace, el sistema lo lleva directo a registrarse y selecciona TU club automáticamente. No tienen que buscar nada — solo poner correo, contraseña y datos básicos.",
      },
      {
        title: 'Aprueba las solicitudes en "Solicitudes de unión"',
        detail:
          'En el menú lateral entra a "Miembros" → "Solicitudes". Verás las personas esperando entrar. Revisa nombre/edad y aprueba.',
        warning:
          "Aprueba SOLO a personas que reconoces. Si llega un nombre raro, rechaçalo. Tú eres el filtro.",
      },
      {
        title: "(Alternativa) Carga manual para los que no tienen correo",
        detail:
          'Si tienes niños/Aventureros sin correo o personas que no van a usar la app, entra a "Miembros" → "Agregar miembro" y crea el registro tú mismo con nombre, fecha de nacimiento y sexo. La categoría (Aventurero / Conquistador / Guía Mayor) se calcula sola.',
        tip: "Usa esta vía solo cuando NO se pueda hacer por enlace. La invitación es siempre más rápida y precisa.",
      },
      {
        title: "Evita duplicados",
        detail:
          "Si alguien ya se registró con el enlace, NO lo crees a mano otra vez. Busca primero en tu lista de miembros. El sistema bloquea duplicados con mismo nombre + fecha de nacimiento.",
        warning:
          "Duplicados rompen los reportes, los pagos y el ranking. Si dudas, busca primero.",
      },
    ],
    expectedResult:
      "La mayoría de tus miembros se registran solos en minutos y tú solo apruebas. La lista de miembros queda completa, sin que tú hayas tecleado un solo nombre.",
    cta: { label: "Ir al Dashboard a copiar mi enlace", to: "/dashboard" },
  },
  {
    id: "registro-evento",
    number: 3,
    icon: Calendar,
    title: "Inscribe a tu club en el campamento",
    subtitle:
      "El paso clave: inscribir a tus miembros al evento y subir el comprobante de pago.",
    goal: "Tener todos tus miembros inscritos al CAMPAMENTO DEMO 2026 con sus pagos cargados.",
    prerequisites: [
      "Miembros ya creados (paso 2).",
      "Comprobante de pago en PDF o imagen (durante la demo te damos uno de prueba).",
    ],
    substeps: [
      {
        title: 'Entra a "Eventos" en el menú',
        detail:
          'Verás la lista de eventos disponibles. Busca "CAMPAMENTO DEMO 2026".',
      },
      {
        title: "Abre el evento",
        detail:
          "Verás fechas, cupos disponibles, requisitos, precio por persona y cupos restantes en tiempo real.",
        tip: "Si los cupos llegan a 0 ya no podrás inscribir más, así que llega temprano cuando abran el evento real.",
      },
      {
        title: 'Haz clic en "Inscribir miembros"',
        detail:
          "Se abre un selector con todos los miembros de tu club que cumplen la categoría del evento.",
      },
      {
        title: "Selecciona a los que van a asistir",
        detail:
          "Marca la casilla de cada miembro. Puedes seleccionar varios a la vez. Confirma.",
        warning:
          "Si intentas inscribir al mismo miembro dos veces (incluso desde otro dispositivo o sesión), el sistema lo rechaza. Eso es a propósito y no es un error.",
      },
      {
        title: "Sube el comprobante de pago",
        detail:
          'En la sección "Pagos del evento" haz clic en "Subir comprobante". Adjunta el PDF/imagen y escribe monto + referencia bancaria.',
        tip: 'Puedes subir UN solo pago para varios miembros. En "miembros cubiertos" marcas a todos los que pagaste juntos.',
      },
      {
        title: "Espera la aprobación del pago",
        detail:
          'Estado inicial: "Pendiente". Cuando la asociación valida, pasa a "Aprobado" y la inscripción queda CONFIRMADA. Verás la actualización sin recargar.',
      },
      {
        title: "Revisa el ranking",
        detail:
          "En el evento aparece el ranking en vivo: posición de tu club, total inscritos, total pagado.",
      },
    ],
    expectedResult:
      "Todos tus miembros aparecen como inscritos. Cuando aprueben los pagos, todos quedan en estado verde y subes posiciones en el ranking.",
    cta: { label: "Ir a Eventos", to: "/events" },
  },
];

// ============================================================================
// CONTENIDO — TRACK MIEMBRO
// ============================================================================

const MIEMBRO_STEPS: Step[] = [
  {
    id: "miembro-cuenta",
    number: 1,
    icon: Link2,
    title: "Regístrate con el enlace que te envió tu director",
    subtitle:
      "La forma más fácil y rápida: usa el enlace que tu director compartió en el grupo del club. Te queda vinculado automáticamente — no tienes que buscar nada.",
    goal: "Tener una cuenta activa, vinculada a tu club, para ver tu progreso, eventos y especialidades.",
    prerequisites: [
      "El enlace de invitación de tu director (lo manda por WhatsApp/grupo del club).",
      "Un correo electrónico (si eres menor de edad, usa el de tus padres).",
    ],
    substeps: [
      {
        title: "Abre el enlace que envió tu director",
        detail:
          "Es un link que termina en /setup?invite=XXXXXX. Ábrelo desde tu celular o computador. Si no te llegó, pídeselo a tu director — NO lo busques en otro lado.",
        tip: "Funciona perfecto desde el celular: solo toca el enlace en el chat de WhatsApp.",
      },
      {
        title: "Regístrate (el sistema reconoce tu club automáticamente)",
        detail:
          "Llena nombre, correo y contraseña (mínimo 8 caracteres). Como entraste por el enlace, NO necesitas buscar tu club — ya está seleccionado.",
        warning:
          "Si eres menor de 13 años, debe registrarte un padre o tutor con SU correo. La cuenta queda a nombre del menor pero el adulto la administra.",
      },
      {
        title: "Verifica tu correo",
        detail:
          "Te llega un correo con un enlace. Haz clic. Sin esto la cuenta no se activa. Revisa SPAM si no llega en 2 minutos.",
      },
      {
        title: "Espera la aprobación del director",
        detail:
          'Verás la pantalla "Pendiente de aprobación". Tu director recibe una notificación y te aprueba en segundos. Si pasa más de 1 hora, avísale por WhatsApp.',
      },
      {
        title: "(Si NO tienes el enlace) Pide que te creen a mano",
        detail:
          'Si por alguna razón no puedes usar el enlace, pídele a tu director que te cree manualmente desde "Miembros → Agregar miembro". En ese caso no tendrás cuenta propia para entrar a la app, pero sí quedarás registrado para asistir al campamento.',
      },
    ],
    expectedResult:
      "Tu cuenta queda activa y vinculada al club correcto. Entras al Dashboard del miembro y puedes ver el evento del campamento.",
    cta: { label: "Ya tengo el enlace · Registrarme", to: "/register" },
  },
  {
    id: "miembro-evento",
    number: 2,
    icon: Calendar,
    title: "Confirma tu asistencia al campamento",
    subtitle:
      "Tu director te inscribe — tú solo confirmas y revisas tu estado.",
    goal: "Saber si estás inscrito, si tu pago está aprobado, y ver los detalles del evento.",
    substeps: [
      {
        title: 'Entra a "Eventos" en tu menú',
        detail: "Verás los eventos a los que tu club te ha inscrito.",
      },
      {
        title: "Abre el campamento",
        detail:
          "Verás: fechas, lugar, qué llevar, tu estado de inscripción, tu estado de pago y los datos del responsable de tu club.",
      },
      {
        title: "Revisa tu estado",
        detail:
          'Estado inscripción: "Pendiente" / "Aprobada". Estado pago: "Pendiente" / "Aprobado". Ambos deben quedar verdes para asistir.',
        warning:
          "Si tu estado dice 'Rechazado' habla con tu director ANTES del evento — no esperes al día.",
      },
      {
        title: "Activa notificaciones",
        detail:
          "Acepta los permisos de notificaciones del navegador para recibir avisos cuando cambie tu estado o publiquen información del campamento.",
      },
    ],
    expectedResult:
      "Tienes claridad total de si vas o no, y recibes avisos automáticos sin perseguir a tu director.",
    cta: { label: "Ver mis eventos", to: "/events" },
  },
];

// ============================================================================
// COMPONENTES UI
// ============================================================================

function TrackTabs({
  active,
  onChange,
}: {
  active: Track;
  onChange: (t: Track) => void;
}) {
  return (
    <div className="inline-flex rounded-xl bg-gray-100 p-1 mb-8">
      <button
        onClick={() => onChange("director")}
        className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
          active === "director"
            ? "bg-white text-primary-700 shadow-sm"
            : "text-gray-600 hover:text-gray-900"
        }`}
      >
        Soy Director
      </button>
      <button
        onClick={() => onChange("miembro")}
        className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all ${
          active === "miembro"
            ? "bg-white text-primary-700 shadow-sm"
            : "text-gray-600 hover:text-gray-900"
        }`}
      >
        Soy Miembro
      </button>
    </div>
  );
}

function StepperBar({
  steps,
  current,
  onPick,
}: {
  steps: Step[];
  current: number;
  onPick: (i: number) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2 mb-10 overflow-x-auto pb-2">
      {steps.map((s, i) => {
        const isCurrent = i === current;
        const isDone = i < current;
        return (
          <button
            key={s.id}
            onClick={() => onPick(i)}
            className={`flex-1 min-w-[140px] text-left transition-all ${
              isCurrent ? "scale-105" : "opacity-80 hover:opacity-100"
            }`}
          >
            <div className="flex items-center gap-3">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm shrink-0 transition-colors ${
                  isCurrent
                    ? "bg-primary-600 text-white shadow-lg shadow-primary-200"
                    : isDone
                      ? "bg-green-500 text-white"
                      : "bg-gray-200 text-gray-600"
                }`}
              >
                {isDone ? <CheckCircle2 className="w-5 h-5" /> : s.number}
              </div>
              <div className="hidden sm:block">
                <div
                  className={`text-xs font-semibold uppercase tracking-wide ${
                    isCurrent ? "text-primary-600" : "text-gray-500"
                  }`}
                >
                  Paso {s.number}
                </div>
                <div
                  className={`text-sm font-medium leading-tight ${
                    isCurrent ? "text-gray-900" : "text-gray-600"
                  }`}
                >
                  {s.title}
                </div>
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

function SubStepCard({ sub, index }: { sub: SubStep; index: number }) {
  return (
    <div className="flex gap-4 p-5 bg-white rounded-xl border border-gray-200 hover:border-primary-200 hover:shadow-sm transition-all">
      <div className="shrink-0 w-8 h-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center font-bold text-sm">
        {index + 1}
      </div>
      <div className="flex-1 space-y-2">
        <h4 className="font-semibold text-gray-900">{sub.title}</h4>
        <p className="text-gray-600 text-sm leading-relaxed">{sub.detail}</p>
        {sub.tip && (
          <div className="flex gap-2 mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
            <Lightbulb className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-900 leading-relaxed">
              <span className="font-semibold">Tip:</span> {sub.tip}
            </p>
          </div>
        )}
        {sub.warning && (
          <div className="flex gap-2 mt-2 p-3 rounded-lg bg-red-50 border border-red-200">
            <AlertTriangle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
            <p className="text-xs text-red-900 leading-relaxed">
              <span className="font-semibold">Importante:</span> {sub.warning}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function StepContent({ step }: { step: Step }) {
  const Icon = step.icon;
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-50 via-white to-accent-50 rounded-2xl p-6 sm:p-8 border border-primary-100">
        <div className="flex flex-col sm:flex-row gap-4 items-start">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center shrink-0 shadow-lg">
            <Icon className="w-7 h-7 text-white" />
          </div>
          <div className="flex-1">
            <div className="text-xs font-bold text-primary-600 uppercase tracking-wider mb-1">
              Paso {step.number}
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              {step.title}
            </h2>
            <p className="text-gray-600">{step.subtitle}</p>
          </div>
        </div>

        <div className="mt-5 p-4 bg-white/70 rounded-xl border border-white">
          <div className="flex gap-2 items-start">
            <Trophy className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
            <div>
              <div className="text-xs font-bold text-gray-700 uppercase tracking-wide">
                Meta de este paso
              </div>
              <p className="text-sm text-gray-800 mt-1">{step.goal}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Prerequisites */}
      {step.prerequisites && step.prerequisites.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
          <div className="flex gap-2 items-center mb-3">
            <ClipboardList className="w-5 h-5 text-blue-600" />
            <h3 className="font-bold text-blue-900">Necesitas tener listo:</h3>
          </div>
          <ul className="space-y-2">
            {step.prerequisites.map((p, i) => (
              <li key={i} className="flex gap-2 text-sm text-blue-900">
                <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Substeps */}
      <div>
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Sigue estos sub-pasos en orden:
        </h3>
        <div className="space-y-3">
          {step.substeps.map((sub, i) => (
            <SubStepCard key={i} sub={sub} index={i} />
          ))}
        </div>
      </div>

      {/* Expected result */}
      <div className="bg-green-50 border border-green-200 rounded-xl p-5">
        <div className="flex gap-2 items-start">
          <ShieldCheck className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          <div>
            <h3 className="font-bold text-green-900">Resultado esperado</h3>
            <p className="text-sm text-green-900 mt-1">{step.expectedResult}</p>
          </div>
        </div>
      </div>

      {/* CTA */}
      {step.cta && (
        <div className="flex justify-center pt-2">
          <Link to={step.cta.to}>
            <Button size="lg">
              {step.cta.label}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// PÁGINA PRINCIPAL
// ============================================================================

export default function GuiaDemoPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTrack = (searchParams.get("track") as Track) || "director";
  const initialStep = parseInt(searchParams.get("paso") || "1", 10) - 1;

  const [track, setTrack] = useState<Track>(initialTrack);
  const [stepIndex, setStepIndex] = useState<number>(
    Math.max(0, isNaN(initialStep) ? 0 : initialStep),
  );

  const steps = useMemo(
    () => (track === "director" ? DIRECTOR_STEPS : MIEMBRO_STEPS),
    [track],
  );

  // Mantener stepIndex válido si cambia el track
  useEffect(() => {
    if (stepIndex >= steps.length) setStepIndex(0);
  }, [steps.length, stepIndex]);

  // Sync URL con estado
  useEffect(() => {
    setSearchParams({ track, paso: String(stepIndex + 1) }, { replace: true });
  }, [track, stepIndex, setSearchParams]);

  // Scroll al top cuando cambia el paso
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [stepIndex, track]);

  const current = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  const handleTrackChange = (t: Track) => {
    setTrack(t);
    setStepIndex(0);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Top bar simple (no usamos el navbar del landing porque esta página es self-contained) */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-40 backdrop-blur-md bg-white/90">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/branding/insignia_icon.svg"
              alt="Insignia"
              className="w-8 h-8"
            />
            <span className="font-bold text-gray-900 hidden sm:inline">
              Insignia · Guía
            </span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login">
              <Button variant="ghost" size="sm">
                <LogIn className="w-4 h-4" />
                <span className="hidden sm:inline">Iniciar sesión</span>
              </Button>
            </Link>
            <Link to="/register">
              <Button size="sm">
                <span className="hidden sm:inline">Registrarme</span>
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary-50 border border-primary-200 mb-4">
            <PlayCircle className="w-4 h-4 text-primary-600" />
            <span className="text-xs font-semibold text-primary-700 uppercase tracking-wider">
              Guía paso a paso · Demo Campamento
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold text-gray-900 mb-4">
            Aprende a usar Insignia <br className="hidden sm:block" />
            <span className="bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent">
              en 3 pasos
            </span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Sigue esta guía a tu ritmo desde tu celular o computador. Cada paso
            te dice qué hacer, qué esperar y qué hacer si algo falla.
          </p>
        </div>

        {/* Plataformas */}
        <div className="flex justify-center gap-4 mb-8 text-sm text-gray-500">
          <div className="flex items-center gap-1.5">
            <Smartphone className="w-4 h-4" />
            <span>Funciona en móvil</span>
          </div>
          <div className="w-px bg-gray-300" />
          <div className="flex items-center gap-1.5">
            <Mail className="w-4 h-4" />
            <span>Necesitas correo activo</span>
          </div>
        </div>

        {/* Track tabs */}
        <div className="flex justify-center">
          <TrackTabs active={track} onChange={handleTrackChange} />
        </div>

        {/* Stepper */}
        <StepperBar steps={steps} current={stepIndex} onPick={setStepIndex} />

        {/* Step content */}
        <StepContent step={current} />

        {/* Navigation */}
        <div className="flex items-center justify-between gap-4 mt-12 pt-8 border-t border-gray-200">
          <Button
            variant="secondary"
            onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
            disabled={isFirst}
          >
            <ArrowLeft className="w-4 h-4" />
            Paso anterior
          </Button>

          <div className="text-sm text-gray-500 hidden sm:block">
            Paso {stepIndex + 1} de {steps.length}
          </div>

          {isLast ? (
            <Link to={track === "director" ? "/events" : "/dashboard"}>
              <Button>
                ¡Empezar a usar!
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          ) : (
            <Button
              onClick={() =>
                setStepIndex((i) => Math.min(steps.length - 1, i + 1))
              }
            >
              Siguiente paso
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </div>

        {/* Resumen visual al final */}
        {isLast && (
          <div className="mt-12 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-primary-600 to-accent-600 text-white text-center">
            <Trophy className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h3 className="text-2xl font-bold mb-2">
              ¡Listo! Ya conoces el flujo completo
            </h3>
            <p className="text-white/90 mb-6 max-w-xl mx-auto">
              Si te quedó alguna duda durante la demo en vivo, vuelve a esta
              página cuando quieras — el link es{" "}
              <code className="bg-white/20 px-2 py-0.5 rounded">/guia</code>
            </p>
            <div className="flex flex-wrap gap-3 justify-center">
              <Link to="/register">
                <Button variant="secondary" size="lg">
                  Crear mi cuenta ahora
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  size="lg"
                  className="!bg-white/10 !text-white hover:!bg-white/20 border border-white/30"
                >
                  Ya tengo cuenta · Entrar
                </Button>
              </Link>
            </div>
          </div>
        )}

        {/* Recursos extra */}
        <div className="mt-12 grid sm:grid-cols-3 gap-4">
          <ResourceCard
            icon={Building2}
            title="¿Eres líder de asociación?"
            text="Si necesitas crear/aprobar clubes y validar pagos, contáctanos por la opción de soporte."
          />
          <ResourceCard
            icon={CreditCard}
            title="Sobre los pagos"
            text="Aceptamos comprobantes en PDF/JPG. El pago lo valida tu asociación, no automáticamente."
          />
          <ResourceCard
            icon={ShieldCheck}
            title="Tus datos están seguros"
            text="Cada club solo ve sus propios miembros. Las contraseñas están encriptadas."
          />
        </div>
      </main>

      <footer className="border-t border-gray-200 bg-white py-6 mt-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center text-sm text-gray-500">
          <Link to="/" className="hover:text-primary-600 font-medium">
            ← Volver al inicio
          </Link>
        </div>
      </footer>
    </div>
  );
}

function ResourceCard({
  icon: Icon,
  title,
  text,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  text: string;
}) {
  return (
    <div className="p-5 bg-white rounded-xl border border-gray-200">
      <Icon className="w-6 h-6 text-primary-600 mb-3" />
      <h4 className="font-bold text-gray-900 text-sm mb-1">{title}</h4>
      <p className="text-xs text-gray-600 leading-relaxed">{text}</p>
    </div>
  );
}
